import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NAV_ITEMS, NAV_GROUPS, MENU_GATES } from '../navConfig';
import { usePos } from '../context/PosContext';
import { formatDateTime } from '../services/datetime';
import * as db from '../services/db';
import { User } from '../types';
import { APP_VERSION } from '../version';
import eposLogo from '../images/epos-logo.png';
import './Navigation.css';

const Navigation: React.FC = () => {
  const location = useLocation();
  const { config, isOn } = usePos();
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const [operator, setOperator] = useState<User | null>(null);
  // Paths this operator's role may reach; recomputed on login/logout.
  const [accessiblePaths, setAccessiblePaths] = useState<Set<string>>(new Set());
  // Friendly name of this device's terminal mode, for the drawer footer.
  const [deviceModeName, setDeviceModeName] = useState('');

  // Load the operator and their accessible paths, refreshing whenever the
  // logged-in user changes (db.setCurrentUserId fires 'sconnect:auth').
  useEffect(() => {
    let alive = true;
    const load = async () => {
      const user = await db.getCurrentUser();
      const entries = await Promise.all(
        NAV_ITEMS.map(async (i) => [i.path, await db.canAccess(i.path)] as const)
      );
      const roleId = db.getDeviceRole();
      const deviceRole = roleId ? (await db.getRoles()).find((r) => r.id === roleId) : undefined;
      if (!alive) return;
      setOperator(user);
      setAccessiblePaths(new Set(entries.filter(([, ok]) => ok).map(([p]) => p)));
      setDeviceModeName(deviceRole?.name ?? '');
    };
    load();
    window.addEventListener('sconnect:auth', load);
    return () => {
      alive = false;
      window.removeEventListener('sconnect:auth', load);
    };
  }, []);

  // Hide items disabled by Menu Option toggles or outside the current
  // operator's role rights (Users page → Roles & Rights).
  const visibleItems = NAV_ITEMS.filter(
    (i) => (!(i.path in MENU_GATES) || isOn(MENU_GATES[i.path])) && accessiblePaths.has(i.path)
  );

  // Live clock like the Android dashboard header (TextClock).
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Close the drawer after navigating.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const activeLabel =
    NAV_ITEMS.find((i) => i.path === location.pathname)?.label ?? config.storeName;

  // The POS billing screen runs header-less (full-screen, native-app feel). We
  // drop the top app bar there but keep a floating ☰ so the drawer is still
  // reachable for navigating away.
  const hideHeader = location.pathname === '/';

  return (
    <>
      {hideHeader ? (
        <button
          className="hamburger hamburger--floating"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          ☰
        </button>
      ) : (
        <header className="topbar">
          <button className="hamburger" onClick={() => setOpen((o) => !o)} aria-label="Menu">
            ☰
          </button>
          <img className="topbar-logo" src={eposLogo} alt="EPOS" />
          <span className="topbar-title">{activeLabel}</span>
          <span className="topbar-clock">{formatDateTime(now)}</span>
        </header>
      )}

      {open && <div className="drawer-scrim" onClick={() => setOpen(false)} />}

      <nav className={`drawer ${open ? 'drawer--open' : ''}`}>
        <div className="drawer-brand">
          <img className="drawer-logo" src={eposLogo} alt="EPOS — The Points Of Solutions" />
          <h1>{config.storeName}</h1>
          <span className="drawer-version">{APP_VERSION}</span>
          {operator && (
            <span className="drawer-version drawer-operator">
              👤 {operator.name}
              <button
                className="drawer-logout"
                onClick={() => {
                  db.logEvent({ type: 'login', detail: `Logged out ${operator.name}`, userName: operator.name });
                  db.setCurrentUserId(null);
                }}
              >
                Log out
              </button>
            </span>
          )}
        </div>
        <div className="drawer-scroll">
          {NAV_GROUPS.map((group) => {
            const items = visibleItems.filter((i) => i.group === group);
            if (items.length === 0) return null;
            return (
              <div className="drawer-group" key={group}>
                <div className="drawer-group-title">{group}</div>
                <ul className="drawer-list">
                  {items.map((item) => (
                    <li
                      key={item.path}
                      className={`drawer-item ${location.pathname === item.path ? 'active' : ''}`}
                    >
                      <Link to={item.path} className="drawer-link">
                        <span className="drawer-icon">{item.icon}</span>
                        <span className="drawer-label">{item.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          <div className="drawer-group drawer-device-mode">
            <Link to="/device-mode" className="drawer-link drawer-device-link">
              <span className="drawer-icon">🖥️</span>
              <span className="drawer-label">
                This device: <strong>{deviceModeName || 'Not set'}</strong>
                <span className="drawer-device-change"> · change</span>
              </span>
            </Link>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
